import {
    CALENDAR_BASED_QUESTION_SELECTORS as CALENDAR_SELECTOR,
    QUESTION_TYPES,
    SURVEY_QUESTION_CONTENT_NON_QUESTION_BASED_PLACEHOLDERS,
} from '$app-web/constants';
import { QUESTION_CHOICE_BASED_ANSWER_TYPES } from '$app-web/modules/activity-editor/constants';
import type { Answer, LastResponse, ObjectType, Section, SessionResponse, StudySurveysQuestionsList } from '$app-web/types';
import {
    appDateTime,
    areValuesEqual,
    centimetersToFeetAndInches,
    isSuperset,
    kilogramToPound,
    removeArrayDuplicates,
    removeArrayNullishValues,
    roundNumber,
    SuperDate,
} from '$app-web/utils';

import { LENGTH_UNITS, MASS_UNITS } from '../constants';
import { PureStore, StoreQuestion } from '../store/types';
import { isAnswerValueEmpty, shouldPresentQuestion } from './helpers';

function getStandardDateByString(dateString: string, type: string) {
    if (dateString == null || dateString === '') {
        return null;
    }

    if (type === CALENDAR_SELECTOR.TIME) {
        const [hour, minute, second] = dateString.split(':').map(Number);
        return new Date(0, 0, 0, hour, minute, second);
    }

    if (type === CALENDAR_SELECTOR.DATE) {
        const sanitizedDateString = `${dateString} 00:00:00`;
        return appDateTime(sanitizedDateString);
    }
    return appDateTime(dateString);
}

export function getDefaultValue(questionType?: string) {
    switch (questionType) {
        case QUESTION_TYPES.MULTIPLE_CHOICE:
            return [];
        case QUESTION_TYPES.FREE_FORM_TEXT:
        case QUESTION_TYPES.BARCODE:
        case QUESTION_TYPES.INFORMATION:
            return '';
        case QUESTION_TYPES.NUMBER:
        case QUESTION_TYPES.VAS:
        case QUESTION_TYPES.LENGTH:
        case QUESTION_TYPES.MASS:
            return null;
        case QUESTION_TYPES.CALENDAR:
            return [null, null];
        case QUESTION_TYPES.SINGLE_CHOICE:
        case QUESTION_TYPES.IMAGE:
        case QUESTION_TYPES.VIDEO:
        case QUESTION_TYPES.AUDIO:
            return null;
        case QUESTION_TYPES.AUDIO_TEXT:
            return { text: '', audioUrl: null };
        default:
            return null;
    }
}

export function isQuestionUnanswered(question: StoreQuestion) {
    const { questionType, answer, requirePeriod } = question;

    switch (questionType) {
        case QUESTION_TYPES.MULTIPLE_CHOICE:
            return areValuesEqual(answer, []);
        case QUESTION_TYPES.FREE_FORM_TEXT:
        case QUESTION_TYPES.BARCODE:
            return (answer as string).trim() === '';
        case QUESTION_TYPES.NUMBER:
        case QUESTION_TYPES.VAS:
        case QUESTION_TYPES.LENGTH:
        case QUESTION_TYPES.MASS:
            return answer == null;
        case QUESTION_TYPES.CALENDAR: {
            const [start, end] = answer as [Date | null, Date | null];

            if (requirePeriod) {
                return start == null || end == null;
            }
            return start == null;
        }
        case QUESTION_TYPES.SINGLE_CHOICE:
        case QUESTION_TYPES.IMAGE:
        case QUESTION_TYPES.VIDEO:
        case QUESTION_TYPES.AUDIO:
            return answer == null;
        case QUESTION_TYPES.AUDIO_TEXT: {
            const { text, audioUrl } = answer as { text: string; audioUrl: null | string };

            return areValuesEqual({ text: text.trim(), audioUrl }, { text: '', audioUrl: null });
        }
        case QUESTION_TYPES.INFORMATION:
            return false;
        default:
            return answer == null;
    }
}

export function getDefaultAnswer(params: { questionType: string; questionId: number; iteration?: number | null }) {
    const { questionType, questionId, iteration = null } = params;

    const defaultProps = { questionId, iteration, location: { accu: null, lat: null, lng: null, speed: null } };
    const getValue = (value: unknown) => ({ ...defaultProps, value });

    switch (questionType) {
        case QUESTION_TYPES.MULTIPLE_CHOICE:
            return getValue([]);
        case QUESTION_TYPES.FREE_FORM_TEXT:
        case QUESTION_TYPES.BARCODE:
        case QUESTION_TYPES.INFORMATION:
            return getValue('');
        case QUESTION_TYPES.NUMBER:
        case QUESTION_TYPES.VAS:
        case QUESTION_TYPES.LENGTH:
        case QUESTION_TYPES.MASS:
            return getValue(null);
        case QUESTION_TYPES.CALENDAR:
            return getValue([null, null]);
        case QUESTION_TYPES.SINGLE_CHOICE:
        case QUESTION_TYPES.IMAGE:
        case QUESTION_TYPES.VIDEO:
        case QUESTION_TYPES.AUDIO:
            return getValue(null);
        case QUESTION_TYPES.AUDIO_TEXT:
            return getValue({ text: '', audioUrl: null });
        default:
            return getValue(null);
    }
}

export function getAnswerOfQuestion(params: {
    questionId: number;
    questionType: string;
    resp: SessionResponse['resp'];
    iteration?: number | null;
}): {
    value: unknown;
    questionId: number;
    iteration: number | null;
    location: { lat: number | null; lng: number | null; accu: number | null; speed: number | null };
} {
    const { questionId, questionType, iteration = null, resp } = params;
    const location = resp[0]?.location ?? { accu: null, lat: null, lng: null, speed: null };
    const defaultProps = { questionId, iteration, location };
    const getValue = (value: unknown) => ({ ...defaultProps, value });

    if (resp.length === 0) {
        return getDefaultAnswer({ questionType, questionId, iteration });
    }

    const answerContent = resp[0].answerContent ?? '';
    const numericAnswer = roundNumber(Number(answerContent), 2);
    const selector = resp[0].selector ?? '';
    const periodEnd = resp[0].periodEnd ?? '';
    const answerUrl = resp[0].answerUrl ?? null;
    const answerId = resp[0].answerId ? resp[0].answerId.toString() : null;
    const answerIds = resp.filter((item) => item.answerId != null).map((item) => item.answerId!.toString());

    switch (questionType) {
        case QUESTION_TYPES.SINGLE_CHOICE:
            return getValue(answerId);
        case QUESTION_TYPES.MULTIPLE_CHOICE:
            return getValue(answerIds);
        case QUESTION_TYPES.INFORMATION:
        case QUESTION_TYPES.FREE_FORM_TEXT:
        case QUESTION_TYPES.BARCODE:
            return getValue(answerContent);
        case QUESTION_TYPES.NUMBER:
        case QUESTION_TYPES.VAS:
        case QUESTION_TYPES.LENGTH:
        case QUESTION_TYPES.MASS:
            return getValue(numericAnswer);
        case QUESTION_TYPES.CALENDAR:
            return getValue([
                getStandardDateByString(answerContent as string, selector),
                getStandardDateByString(periodEnd, selector),
            ]);
        case QUESTION_TYPES.IMAGE:
        case QUESTION_TYPES.VIDEO:
        case QUESTION_TYPES.AUDIO:
            return getValue(answerUrl);
        case QUESTION_TYPES.AUDIO_TEXT:
            return getValue({
                text: resp[0]?.answerContent ?? resp[1]?.answerContent ?? '',
                audioUrl: resp[0]?.answerUrl ?? resp[1]?.answerUrl ?? null,
            });
        default:
            return getValue(null);
    }
}

export function extractAnswerOfResponse(response: SessionResponse) {
    const { qType: questionType, qId: questionId, resp, iteration } = response;

    if (iteration != null && iteration > 0) {
        return Array.from({ length: iteration + 1 }, (_, index) => {
            const filteredResp = resp.filter((item) => item.iteration === index);

            return getAnswerOfQuestion({ questionId, questionType, iteration: index, resp: filteredResp });
        });
    }

    return getAnswerOfQuestion({ questionId, questionType, resp });
}

export function extractTokensAndPlaceholders(sections: Array<Section>, currentActivityId: number) {
    const QUESTION_TOKEN_REGEX = /Q\d+(_\d+)?/g; // Detects both Qn_m and Qn
    const QUESTION_PLACEHOLDER_REGEX = /\{\{Q\d+_\d+\}\}/g; // Detects both {{Qn_m}} and {{Qn}}

    const sectionsCriteria = sections.map((section) => section.criteria);
    const questions = sections.flatMap((section) => section.questions);
    const questionsCriteria = questions.map((question) => question.criteria);
    const criteriaList = removeArrayNullishValues([...sectionsCriteria, ...questionsCriteria]);
    const detectedTokens = criteriaList.flatMap((criteria) => criteria.match(QUESTION_TOKEN_REGEX) ?? []);
    const normalizedTokens = detectedTokens.map((token) => {
        if (token.includes('_')) {
            return token;
        }
        return token.replace('Q', `Q${currentActivityId}_`);
    });

    const questionContents = questions.map((question) => question.questionContent);
    const placeholders = questionContents.flatMap((content) => content.match(QUESTION_PLACEHOLDER_REGEX) ?? []);
    const normalizedPlaceholders = placeholders.map((token) => {
        if (token.includes('_')) {
            return token;
        }
        return token.replace('Q', `Q${currentActivityId}_`);
    });

    const result = [...normalizedTokens, ...normalizedPlaceholders].map((str) => str.replaceAll('{{', '').replaceAll('}}', ''));

    return removeArrayDuplicates(result);
}

export function getQuestionTokens(data: Array<LastResponse>) {
    const tokens = data.reduce<ObjectType<{ type: string; value: unknown; loopValues: Array<unknown> | null }>>(
        (all, item) => {
            const { activityId, questionId, response } = item;
            const placeholderKey = `Q${activityId}_${questionId}`;
            const answer = extractAnswerOfResponse(response);

            let value = null;
            let loopValues = null;

            if (Array.isArray(answer)) {
                value = answer[answer.length - 1].value;
                loopValues = answer.map((item) => item.value);
            } else {
                value = answer.value;
            }

            return { ...all, [placeholderKey]: { type: response.qType, value, loopValues } };
        },
        {} as ObjectType<{ type: string; value: unknown; loopValues: Array<unknown> | null }>
    );
    return tokens;
}

export function humanizeQuestionAnswer(question: StoreQuestion, t: (value: string) => string) {
    const answer = question.answer;
    const questionOptions = question.answers ?? [];
    const unit = question.prefUnit ?? question.defaultUnit;

    if (isAnswerValueEmpty(answer)) return '';

    switch (question.questionType) {
        case QUESTION_TYPES.INFORMATION:
        case QUESTION_TYPES.BARCODE:
        case QUESTION_TYPES.FREE_FORM_TEXT:
        case QUESTION_TYPES.NUMBER:
        case QUESTION_TYPES.VAS:
            return ((answer ?? '') as number | string).toString();
        case QUESTION_TYPES.LENGTH:
            if (unit === LENGTH_UNITS.FT_IN) {
                const { feet, inches } = centimetersToFeetAndInches(answer as number);

                return `${feet} ft ${inches} in`;
            } else {
                return `${answer as number} cm`;
            }
        case QUESTION_TYPES.MASS:
            if (unit === MASS_UNITS.LBS) {
                const lbs = kilogramToPound(answer as number);

                return `${lbs} lbs`;
            } else {
                return `${answer as number} kg`;
            }
        case QUESTION_TYPES.CALENDAR: {
            const [start, end] = answer as [Date, Date | null];

            const startDateString = SuperDate.humanizeDateTime(start);

            if (question.requirePeriod) {
                const endDateString = SuperDate.humanizeDateTime(end as Date);
                const fromLabel = t('webActivities:from_label').toLocaleUpperCase();
                const toLabel = t('webActivities:to_label').toLocaleUpperCase();

                if (question.selector === 'time') {
                    return `${fromLabel} ${startDateString.slice(-5)} ${toLabel} ${endDateString.slice(-5)}`;
                }
                if (question.selector === 'date') {
                    return `${fromLabel} ${startDateString.slice(0, -7)} ${toLabel} ${endDateString.slice(0, -7)}`;
                }
                return `${fromLabel} ${startDateString} ${toLabel} ${endDateString}`;
            }

            if (question.selector === 'time') {
                return startDateString.slice(-5);
            }
            if (question.selector === 'date') {
                return startDateString.slice(0, -7);
            }
            return startDateString;
        }
        case QUESTION_TYPES.SINGLE_CHOICE: {
            const matchedOption = questionOptions.find((option) => option.answerId.toString() === (answer as string));
            return matchedOption?.answerContent ?? '';
        }
        case QUESTION_TYPES.MULTIPLE_CHOICE: {
            const answersType = questionOptions[0]?.answerType;

            if (answersType !== QUESTION_CHOICE_BASED_ANSWER_TYPES.TEXT) {
                return '';
            }

            return (answer as Array<string>)
                .map((selectedId) => {
                    const matchedOption = questionOptions.find((option) => option.answerId.toString() === selectedId);

                    return matchedOption?.answerContent ?? '';
                })
                .join(', ');
        }
        case QUESTION_TYPES.AUDIO_TEXT: {
            const { text = '' } = answer as { text: string; audioUrl: null | string };

            return text.trim();
        }
        default:
            return '';
    }
}

export function normalizeQuestionContent(questionContent: string, placeholders: ObjectType<string>) {
    const SPECIAL_PLACEHOLDERS = Object.values(SURVEY_QUESTION_CONTENT_NON_QUESTION_BASED_PLACEHOLDERS);
    const QUESTION_PLACEHOLDER_REGEX = /\{\{Q\d+(_\d+)?\}\}/g; // Detects both {{Qn_m}} and {{Qn}}
    const questionsPlaceholders = removeArrayDuplicates(questionContent.match(QUESTION_PLACEHOLDER_REGEX) ?? []);

    const finalQuestionContent = [...SPECIAL_PLACEHOLDERS, ...questionsPlaceholders].reduce(
        (result, token) => result.replaceAll(token, placeholders[token] ?? ''),
        questionContent
    );

    return finalQuestionContent;
}

export function normalizeQuestionsForStore(sections: Array<Section>): Array<StoreQuestion> {
    const questionsWithSectionId = sections.flatMap((section) => {
        const updatedQuestions = section.questions.map((question) => ({
            sectionId: section.id,
            sectionCriteria: section.criteria,
            sectionLoopSourceQid: section.loopSourceQid,
            sectionNextSection: section.nextSection,
            ...question,
        }));

        return updatedQuestions;
    });

    return questionsWithSectionId.map((question) => ({
        ...question,
        answer: getDefaultValue(question.questionType),
        isHiddenBySectionsRandomization: false,
        isHiddenBySectionCriteria: false,
        isHiddenByNoSectionLoop: false,
        isHiddenByRandomization: false,
        isHiddenBySectionsFlow: false,
        isHiddenByCriteria: false,
        isHiddenByFlow: null,
        isEnabledByAnswers: false,
        iteration: null,
    }));
}

export function applySurveyLoopSections(questions: Array<StoreQuestion>): Array<StoreQuestion> {
    const filteredQuestions: Array<StoreQuestion> = questions
        .filter((question, index, self) => {
            const questionId = question.questionId;
            const itemIndex = self.findIndex((item) => item.questionId === questionId);

            return itemIndex === index;
        })
        .map((question) => ({ ...question, iteration: null }));

    const questionsWithIteration: Array<StoreQuestion> = filteredQuestions.flatMap((question, _, self) => {
        const loopSourceQuestion = self.find(({ questionId }) => questionId === question.sectionLoopSourceQid);

        if (loopSourceQuestion == null) {
            return [question];
        } else {
            // Answer refers to the value of a number question or multiple answer question
            const answer = (loopSourceQuestion.answer as number | Array<string>) ?? 0;
            const size = Array.isArray(answer) ? answer.length : answer;

            if (size > 0) {
                return Array.from({ length: size }, (_, index) => {
                    const questionInClone = questions.find((item) =>
                        isSuperset(item, { questionId: question.questionId, iteration: index })
                    );

                    return {
                        ...question,
                        isHiddenByNoSectionLoop: false,
                        iteration: index,
                        answer: questionInClone ? questionInClone.answer : getDefaultValue(question.questionType),
                    };
                });
            } else {
                return [{ ...question, isHiddenByNoSectionLoop: true, iteration: null }];
            }
        }
    });

    const reorderedQuestions: Array<StoreQuestion> = questionsWithIteration.sort((a, b) => {
        if (a.sectionId === b.sectionId && typeof a.iteration === 'number' && typeof b.iteration === 'number') {
            if (a.iteration > b.iteration) {
                return 1;
            } else if (a.iteration < b.iteration) {
                return -1;
            } else {
                return 0;
            }
        }
        return 0;
    });

    return reorderedQuestions;
}

export function getValidQuestions(questions: Array<StoreQuestion>) {
    return questions.filter((question) => {
        if (!question.enabled && !question.isEnabledByAnswers) {
            return false;
        } else if (question.isHiddenByCriteria) {
            return false;
        } else if (question.isHiddenBySectionsRandomization) {
            return false;
        } else if (question.isHiddenByRandomization) {
            return false;
        } else if (question.isHiddenBySectionCriteria) {
            return false;
        } else if (question.isHiddenBySectionsFlow && question.isHiddenByFlow !== false) {
            return false;
        } else if (question.isHiddenByNoSectionLoop) {
            return false;
        } else if (
            (question.questionType === QUESTION_TYPES.MULTIPLE_CHOICE ||
                question.questionType === QUESTION_TYPES.SINGLE_CHOICE) &&
            question.answers?.length === 0
        ) {
            return false;
        } else if (question.isHiddenByFlow === true) {
            return false;
        } else {
            return true;
        }
    });
}

export function extractPlaceholderByResponse(lastResponse: LastResponse, t: (value: string) => string) {
    const responses = lastResponse.response.resp;
    const resp = responses[0];

    switch (lastResponse.response.qType) {
        case QUESTION_TYPES.INFORMATION:
        case QUESTION_TYPES.BARCODE:
        case QUESTION_TYPES.FREE_FORM_TEXT:
        case QUESTION_TYPES.NUMBER:
        case QUESTION_TYPES.VAS:
            return ((resp.answerContent ?? '') as number | string).toString();
        case QUESTION_TYPES.LENGTH:
            return `${resp.answerContent as number} cm`;
        case QUESTION_TYPES.MASS:
            return `${resp.answerContent as number} kg`;
        case QUESTION_TYPES.SINGLE_CHOICE: {
            const { answerUrl = '' } = resp;

            if (answerUrl !== '') {
                return `${t('webActivities:option')} ${resp.answerId}`;
            } else {
                return resp.answerContent as string;
            }
        }
        case QUESTION_TYPES.CALENDAR: {
            const { selector } = lastResponse.response;
            const start = (responses[0]?.answerContent ?? '') as string;
            const end = (responses[1]?.answerContent ?? null) as string | null;

            if (end != null) {
                const fromLabel = t('webActivities:from_label').toLocaleUpperCase();
                const toLabel = t('webActivities:to_label').toLocaleUpperCase();

                if (selector === 'time') {
                    return `${fromLabel} ${start.slice(-5)} ${toLabel} ${end.slice(-5)}`;
                }
                if (selector === 'date') {
                    return `${fromLabel} ${start.slice(0, -6)} ${toLabel} ${end.slice(0, -6)}`;
                }
                return `${fromLabel} ${start} ${toLabel} ${end}`;
            }

            if (selector === 'time') {
                return start.slice(-5);
            }
            if (selector === 'date') {
                return start.slice(0, -6);
            }
            return start;
        }
        case QUESTION_TYPES.MULTIPLE_CHOICE: {
            const isImageBase = resp.answerUrl != null && resp.answerUrl !== '';

            if (isImageBase) {
                return '';
            } else {
                return responses.map((option) => option.answerContent).join(', ');
            }
        }
        default:
            return '';
    }
}

const findCanonicalQuestion = (allQuestions: Array<StoreQuestion>, questionId: number): StoreQuestion | undefined => {
    // Prioritize a non-iterated version of the question if available.
    const nonIteratedQuestion = allQuestions
        .filter((question) => shouldPresentQuestion(question))
        .find((question) => question.questionId === questionId && question.iteration === null);
    if (nonIteratedQuestion) {
        return nonIteratedQuestion;
    }
    // Fallback to the first question found with the matching ID, if any.
    return allQuestions.find((question) => question.questionId === questionId);
};

export function populateCarryForwardOptions(
    targetQuestion: StoreQuestion,
    allCurrentSurveyQuestions: Array<StoreQuestion>,
    externalSurveysQuestions: StudySurveysQuestionsList['surveys'],
    allTokens: PureStore['tokens'] | undefined,
    activityId: number
): Array<Answer> {
    const { carryForwardQuestion: carryForwardInfo } = targetQuestion;
    if (!carryForwardInfo?.questionId) {
        return targetQuestion.answers || [];
    }

    // Helper functions
    const removeDuplicateAndEmptyOptions = (options: Array<Answer>): Array<Answer> => {
        const seen = new Set<string>();
        return options.filter((option) => {
            // For URL type, check answerUrl
            if (option.answerType === 'url') {
                const url = option.answerUrl;
                if (url == null || url === '' || url === undefined) {
                    return false;
                }
                if (seen.has(url)) {
                    return false;
                }
                seen.add(url);
                return true;
            }
            // For text type, check answerContent
            const content = option.answerContent;
            if (content == null || content === '' || content === undefined) {
                return false;
            }
            if (seen.has(content)) {
                return false;
            }
            seen.add(content);
            return true;
        });
    };

    const createAnswerOption = (value: string, index: number, originalOption?: Answer, isImageType = false): Answer => {
        // If we have an original option, preserve its type and structure
        if (originalOption) {
            if (originalOption.answerType === 'url') {
                return {
                    ...originalOption,
                    answerId: index + 1,
                    answerUrl: typeof value === 'string' ? value : '',
                };
            }
            return {
                ...originalOption,
                answerId: index + 1,
                answerContent: formatAnswerContent(value),
            };
        }

        // For new options (like creating options from looped questions)
        return {
            answerId: index + 1,
            ...(isImageType ? { answerUrl: value } : { answerContent: formatAnswerContent(value) }),
            answerType: isImageType ? 'url' : 'text',
            enableQuestion: '',
            nextSection: 0,
            answerContentTranslation: {},
        };
    };

    const formatAnswerContent = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (typeof value === 'number') return value.toString();
        if (value instanceof Date) return value.toISOString();
        if (value === null || value === undefined) return '';
        if (Array.isArray(value)) {
            return value
                .filter((item) => item != null)
                .map((date) => SuperDate.humanizeDateTime(date.toISOString()))
                .join(', ');
        }
        return JSON.stringify(value);
    };

    const getSelectedOptionIds = (answer: unknown): Array<string> => {
        if (Array.isArray(answer)) {
            return answer.map((id) => String(id));
        }
        return answer != null ? [String(answer)] : [];
    };

    const filterOptionsByCarryForwardType = (
        options: Array<Answer>,
        selectedIds: Array<string>,
        type: 'checked' | 'unchecked' | null
    ): Array<Answer> => {
        if (type !== 'checked' && type !== 'unchecked') {
            return options;
        }

        // For unchecked type, we need to verify that at least one option was selected
        // This ensures the sender question wasn't skipped
        if (type === 'unchecked' && selectedIds.length === 0) {
            return [];
        }

        return options.filter((option) => {
            const isSelected = selectedIds.includes(String(option.answerId));
            return type === 'checked' ? isSelected : !isSelected;
        });
    };

    const processLoopedQuestions = (iterations: Array<StoreQuestion>): Array<Answer> => {
        return iterations
            .sort((a, b) => (a.iteration ?? 0) - (b.iteration ?? 0))
            .map((iteration, index) =>
                createAnswerOption(iteration.answer as string, index, undefined, iteration.questionType === QUESTION_TYPES.IMAGE)
            );
    };

    // Main logic
    const senderQuestionIdString = carryForwardInfo.questionId;
    const isInternalCarryForward = !senderQuestionIdString.includes('_');
    const senderQuestionId = parseInt(senderQuestionIdString.replace('Q', ''), 10);

    // Handle internal carry-forward questions
    if (isInternalCarryForward) {
        // 1. Try current survey for both regular and looped options
        const senderQuestion = findCanonicalQuestion(allCurrentSurveyQuestions, senderQuestionId);
        // Only when the receiver question comes after the sender question, otherwise always get the answer from previous sessions (tokens used in blow lines)
        if (senderQuestion?.orderId && targetQuestion.orderId > senderQuestion?.orderId) {
            // First check regular options from current survey
            if (senderQuestion?.answers) {
                const selectedIds = getSelectedOptionIds(senderQuestion.answer);
                const filteredOptions = filterOptionsByCarryForwardType(
                    senderQuestion.answers,
                    selectedIds,
                    carryForwardInfo.type
                );
                const result = removeDuplicateAndEmptyOptions(filteredOptions);
                if (result.length > 0) {
                    return result;
                }
            }

            // Then check looped questions in current survey
            const loopedQuestions = allCurrentSurveyQuestions.filter(
                (questionItem) =>
                    questionItem.questionId === senderQuestionId &&
                    typeof questionItem.iteration === 'number' &&
                    shouldPresentQuestion(questionItem)
            );

            if (loopedQuestions.length > 0) {
                // For looped questions, we need to preserve the answer type from the original options
                const loopedOptions = removeDuplicateAndEmptyOptions(
                    processLoopedQuestions(loopedQuestions).map((option, index) => {
                        return createAnswerOption(
                            option.answerContent || option.answerUrl || '',
                            index,
                            option,
                            option.answerType === 'url'
                        );
                    })
                );
                if (loopedOptions.length > 0) {
                    return loopedOptions;
                }
            }
        }

        // 2. Only if both current survey checks fail, try tokens
        const tokenKey = `Q${activityId}_${senderQuestionId}`;
        const tokenResponse = allTokens?.[tokenKey];
        if (tokenResponse) {
            // Handle looped questions from tokens
            if (tokenResponse.loopValues != null) {
                const originalOptions = senderQuestion?.answers || [];

                return removeDuplicateAndEmptyOptions(
                    tokenResponse.loopValues.map((value, index) =>
                        createAnswerOption(
                            value as string,
                            index,
                            originalOptions[index % originalOptions.length],
                            tokenResponse.type === 'image'
                        )
                    )
                );
            }

            // Handle regular questions from tokens
            if (senderQuestion?.answers) {
                const selectedIds = getSelectedOptionIds(tokenResponse.value);
                const filteredOptions = filterOptionsByCarryForwardType(
                    senderQuestion.answers,
                    selectedIds,
                    carryForwardInfo.type
                );
                const result = removeDuplicateAndEmptyOptions(filteredOptions);
                if (result.length > 0) {
                    return result;
                }
            }
        }
        return [];
    }

    // Handle external carry-forward questions
    const tokenResponse = allTokens?.[senderQuestionIdString];
    if (!tokenResponse) {
        return [];
    }

    // Handle looped questions from external survey
    if (tokenResponse.loopValues != null) {
        const [surveyId, questionId] = senderQuestionIdString.replace('Q', '').split('_');
        const externalQuestion = externalSurveysQuestions
            .find((survey) => survey.id.toString() === surveyId)
            ?.questions.find((q) => q.id === parseInt(questionId, 10));
        const originalOptions = externalQuestion?.answers || [];
        return removeDuplicateAndEmptyOptions(
            tokenResponse.loopValues.map((value, index) =>
                createAnswerOption(
                    value as string,
                    index,
                    originalOptions[index % originalOptions.length],
                    tokenResponse.type === 'image'
                )
            )
        );
    }

    // Handle regular questions from external survey
    const [surveyId, questionId] = senderQuestionIdString.replace('Q', '').split('_');
    const externalQuestion = externalSurveysQuestions
        .find((survey) => survey.id.toString() === surveyId)
        ?.questions.find((q) => q.id === parseInt(questionId, 10));

    if (!externalQuestion?.answers) {
        return [];
    }

    const selectedIds = getSelectedOptionIds(tokenResponse.value);
    const filteredOptions = filterOptionsByCarryForwardType(externalQuestion.answers, selectedIds, carryForwardInfo.type);
    return removeDuplicateAndEmptyOptions(filteredOptions);
}
